import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Clock, 
  AlertTriangle, 
  Edit3, 
  Calendar, 
  RefreshCw,
  MessageSquare,
  Image as ImageIcon,
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Save,
  X,
  ThumbsUp,
  Eye,
  Trash2,
  Users,
  Send,
  Hash,
  Copy,
  Download
} from 'lucide-react';
import { formatDate, formatScheduledDate, formatFriendlyDateRange } from '../utils/dateUtils';
import SocialMediaPreviewModal from '../components/SocialMediaPreviewModal';
import SMSPreviewModal from '../components/SMSPreviewModal';

// Component to render SMS content with clickable short links
const SMSContentWithShortLinks = ({ content, className = "" }) => {
  if (!content) return <div className={className}></div>;
  
  // Convert URLs to clickable links using React elements instead of raw HTML
  const renderContentWithLinks = (text) => {
    if (!text) return text;
    
    // Split the text by URLs to create an array of text and URL parts
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        // This is a URL, create a clickable link
        let linkText = 'Visit Link';
        let linkHref = part;
        
        // Customize link text based on URL type
        if (part.includes('book')) {
          linkText = 'Book Now';
          linkHref = 'https://petsandvetsanimalhospital.com/book';
        } else if (part.includes('petsandvets') || part.includes('yourvet')) {
          linkText = 'Visit Website';
          linkHref = 'https://petsandvetsanimalhospital.com';
        }
        
        return (
          <a 
            key={index}
            href={linkHref}
            target="_blank" 
            rel="noopener noreferrer"
            className="text-theme-blue hover:text-blue-800 font-medium underline"
          >
            {linkText}
          </a>
        );
      } else {
        // This is regular text
        return part;
      }
    });
  };
  
  return (
    <div className={className}>
      {renderContentWithLinks(content)}
    </div>
  );
};

// Helper function to convert URLs to clickable links
const formatContentWithLinks = (content) => {
  if (!content) return content;
  
  let formattedContent = content;
  
  // First handle text + URL patterns that should be replaced together
  // Replace "book online: URL" or "book: URL" patterns with just "Book Now"
  formattedContent = formattedContent.replace(
    /(book\s*(?:online|now)?:\s*)(https:\/\/petsandvetsanimalhospital\.com\/book\b[^\s]*)/gi,
    '<a href="https://petsandvetsanimalhospital.com/book" target="_blank" rel="noopener noreferrer" class="text-theme-blue hover:text-blue-800 font-medium underline">Book Now</a>'
  );
  
  // Replace "visit: URL" or "website: URL" patterns with just "Visit Our Website"
  formattedContent = formattedContent.replace(
    /(visit|website):\s*(https:\/\/petsandvetsanimalhospital\.com\b[^\s]*)/gi,
    '<a href="https://petsandvetsanimalhospital.com" target="_blank" rel="noopener noreferrer" class="text-theme-blue hover:text-blue-800 font-medium underline">Visit Our Website</a>'
  );
  
  // Handle standalone URLs that weren't caught by the above patterns
  // Book links
  formattedContent = formattedContent.replace(
    /\bhttps:\/\/petsandvetsanimalhospital\.com\/book\b[^\s]*/g,
    '<a href="https://petsandvetsanimalhospital.com/book" target="_blank" rel="noopener noreferrer" class="text-theme-blue hover:text-blue-800 font-medium underline">Book Now</a>'
  );
  
  // Website links (only if not already replaced)
  formattedContent = formattedContent.replace(
    /\bhttps:\/\/petsandvetsanimalhospital\.com\b(?!\/book)[^\s]*/g,
    '<a href="https://petsandvetsanimalhospital.com" target="_blank" rel="noopener noreferrer" class="text-theme-blue hover:text-blue-800 font-medium underline">Visit Our Website</a>'
  );
  
  // Handle other URLs (make them clickable but keep original text)
  formattedContent = formattedContent.replace(
    /\bhttps?:\/\/(?!petsandvetsanimalhospital\.com)[^\s]+/g,
    (match) => `<a href="${match}" target="_blank" rel="noopener noreferrer" class="text-theme-blue hover:text-blue-800 underline">${match}</a>`
  );
  
  return formattedContent;
};

// Component to render content with clickable links (for emails)
const ContentWithLinks = ({ content, className = "" }) => {
  const formattedContent = formatContentWithLinks(content);
  
  return (
    <div 
      className={`whitespace-pre-wrap ${className}`}
      dangerouslySetInnerHTML={{ __html: formattedContent }}
    />
  );
};

// Component for SMS customer preview with proper link styling
const SMSCustomerContentWithLinks = ({ content, className = "" }) => {
  if (!content) return <span className={className}>{content}</span>;
  
  // First handle text + URL patterns that should be replaced together
  let processedContent = content;
  
  // Replace "book online: URL" or "book: URL" patterns with just "Book Now"
  processedContent = processedContent.replace(
    /(book\s*(?:online|now)?:\s*)(https:\/\/petsandvetsanimalhospital\.com\/book\b[^\s]*)/gi,
    'BOOKNOW_PLACEHOLDER'
  );
  
  // Replace "visit: URL" or "website: URL" patterns with just "Visit Our Website"  
  processedContent = processedContent.replace(
    /(visit|website):\s*(https:\/\/petsandvetsanimalhospital\.com\b[^\s]*)/gi,
    'WEBSITE_PLACEHOLDER'
  );
  
  // Split content by URLs and placeholders
  const parts = processedContent.split(/(BOOKNOW_PLACEHOLDER|WEBSITE_PLACEHOLDER|https?:\/\/[^\s]+)/g);
  
  return (
    <span className={className}>
      {parts.map((part, index) => {
        if (part === 'BOOKNOW_PLACEHOLDER') {
          return (
            <a 
              key={index}
              href="https://petsandvetsanimalhospital.com/book" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-theme-blue hover:text-blue-800 font-medium underline"
            >
              Book Now
            </a>
          );
        } else if (part === 'WEBSITE_PLACEHOLDER') {
          return (
            <a 
              key={index}
              href="https://petsandvetsanimalhospital.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-theme-blue hover:text-blue-800 font-medium underline"
            >
              Visit Our Website
            </a>
          );
        } else if (part.match(/^https?:\/\/[^\s]+$/)) {
          // Handle remaining standalone URLs
          let displayText = part;
          if (part.includes('petsandvetsanimalhospital.com/book')) {
            displayText = 'Book Now';
          } else if (part.includes('petsandvetsanimalhospital.com')) {
            displayText = 'Visit Our Website';
          }
          
          return (
            <a 
              key={index}
              href={part} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-theme-blue hover:text-blue-800 font-medium underline"
            >
              {displayText}
            </a>
          );
        } else {
          // Regular text
          return part;
        }
      })}
    </span>
  );
};

// Component to render SMS content with clickable links (React elements instead of HTML)
const SMSContentWithLinks = ({ content, className = "" }) => {
  if (!content) return <span className={className}>{content}</span>;
  
  // First handle text + URL patterns that should be replaced together
  let processedContent = content;
  
  // Replace "book online: URL" or "book: URL" patterns with just "Book Now"
  processedContent = processedContent.replace(
    /(book\s*(?:online|now)?:\s*)(https:\/\/petsandvetsanimalhospital\.com\/book\b[^\s]*)/gi,
    'BOOKNOW_PLACEHOLDER'
  );
  
  // Replace "visit: URL" or "website: URL" patterns with just "Visit Our Website"  
  processedContent = processedContent.replace(
    /(visit|website):\s*(https:\/\/petsandvetsanimalhospital\.com\b[^\s]*)/gi,
    'WEBSITE_PLACEHOLDER'
  );
  
  // Split content by URLs and placeholders
  const parts = processedContent.split(/(BOOKNOW_PLACEHOLDER|WEBSITE_PLACEHOLDER|https?:\/\/[^\s]+)/g);
  
  return (
    <span className={className}>
      {parts.map((part, index) => {
        if (part === 'BOOKNOW_PLACEHOLDER') {
          return (
            <a 
              key={index}
              href="https://petsandvetsanimalhospital.com/book" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white underline hover:text-blue-200 font-medium"
            >
              Book Now
            </a>
          );
        } else if (part === 'WEBSITE_PLACEHOLDER') {
          return (
            <a 
              key={index}
              href="https://petsandvetsanimalhospital.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white underline hover:text-blue-200 font-medium"
            >
              Visit Our Website
            </a>
          );
        } else if (part.match(/^https?:\/\/[^\s]+$/)) {
          // Handle remaining standalone URLs
          let displayText = part;
          if (part.includes('petsandvetsanimalhospital.com/book')) {
            displayText = 'Book Now';
          } else if (part.includes('petsandvetsanimalhospital.com')) {
            displayText = 'Visit Our Website';
          }
          
          return (
            <a 
              key={index}
              href={part} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white underline hover:text-blue-200 font-medium"
            >
              {displayText}
            </a>
          );
        } else {
          // Regular text
          return part;
        }
      })}
    </span>
  );
};

// SMS Content Preview Component
const SMSContentPreview = ({ post }) => {
  const [customerPreview, setCustomerPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCustomerPreview = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers?limit=1`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const customerData = await response.json();
          // Handle paginated response format from API
          if (customerData && customerData.customers && customerData.customers.length > 0) {
            setCustomerPreview(customerData.customers[0]);
          } else if (customerData && Array.isArray(customerData) && customerData.length > 0) {
            // Handle direct array format (fallback)
            setCustomerPreview(customerData[0]);
          } else {
            setError('No customers found in database');
          }
        } else {
          setError('Failed to fetch customer data');
        }
      } catch (error) {
        console.error('Error fetching customer preview:', error);
        setError('Error loading customer data');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerPreview();
  }, []);

  const getPreviewContent = () => {
    if (!post.content) return 'No content available';
    
    let previewContent = post.content;
    
    if (customerPreview) {
      // Replace customer name placeholder with actual customer data
      // Handle different customer name field formats
      const customerName = customerPreview.customer_name || 
                           customerPreview.name || 
                           customerPreview.owner_first_name || 
                           customerPreview.first_name || 
                           'Customer';
      previewContent = previewContent.replace(/\[CUSTOMER_NAME\]/g, customerName);
      
      // Replace pet placeholders with actual pet data
      let petNames = 'Pet';
      
      // Handle pets array format (new format)
      if (customerPreview.pets && Array.isArray(customerPreview.pets) && customerPreview.pets.length > 0) {
        const petNamesList = customerPreview.pets
          .map(pet => pet.pet_name || pet.name || pet.petName)
          .filter(Boolean);
        if (petNamesList.length > 0) {
          if (petNamesList.length === 1) {
            petNames = petNamesList[0];
          } else if (petNamesList.length === 2) {
            petNames = `${petNamesList[0]} and ${petNamesList[1]}`;
          } else {
            petNames = `${petNamesList.slice(0, -1).join(', ')}, and ${petNamesList[petNamesList.length - 1]}`;
          }
        }
      }
      // Handle legacy pet_name format (fallback)
      else if (customerPreview.pet_name && customerPreview.pet_name.trim()) {
        petNames = customerPreview.pet_name.trim();
      }
      // Handle other pet field formats
      else if (customerPreview.petName && customerPreview.petName.trim()) {
        petNames = customerPreview.petName.trim();
      }
      
      previewContent = previewContent.replace(/\[PET_NAME\]/g, petNames);
      previewContent = previewContent.replace(/\[PET_NAMES\]/g, petNames);
    } else {
      // No customer data available - show message instead of dummy data
      return 'No customer data available for preview. Please add customers to the database to see personalized preview.';
    }
    
    // Replace link placeholder with actual SMS link
    const smsLink = post.sms_link || 'https://petsandvetsanimalhospital.com';
    previewContent = previewContent.replace(/\[LINK\]/g, smsLink);
    
    // Replace global placeholders with actual business information
    previewContent = previewContent.replace(/\[BUSINESS_NAME\]/g, 'Pets and Vets Animal Hospital');
    previewContent = previewContent.replace(/\[PHONE_NUMBER\]/g, '(703) 957-3297');
    previewContent = previewContent.replace(/\[WEBSITE_LINK\]/g, 'https://petsandvetsanimalhospital.com');
    previewContent = previewContent.replace(/\[BOOK_NOW_LINK\]/g, 'https://petsandvetsanimalhospital.com/book');
    previewContent = previewContent.replace(/\[BUSINESS_ADDRESS\]/g, '43114 Peacock Market Plaza, Suite F110, South Riding, VA 20152');
    
    return previewContent;
  };

  if (loading) {
    return (
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="text-sm text-orange-600 mb-2">📱 SMS Preview</div>
        <div className="text-gray-500">Loading customer data from database...</div>
      </div>
    );
  }

  if (error || !customerPreview) {
    // For non-personalized SMS or when no customer data available
    const isPersonalized = post.marketing_sms_personalized !== false && 
                          (post.content && (post.content.includes('[CUSTOMER_NAME]') || 
                                           post.content.includes('[PET_NAME]') || 
                                           post.content.includes('[PET_NAMES]')));
    
    return (
      <div className="space-y-4">
        {/* Show SMS content directly for non-personalized messages */}
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="text-sm text-orange-600 mb-2 font-medium">
            📱 {isPersonalized ? 'SMS Preview Unavailable' : 'SMS Preview Content'}
          </div>
          {isPersonalized ? (
            <div className="text-orange-800 text-sm">
              {error || 'No customers found in database. Please add customers to see personalized SMS preview.'}
            </div>
          ) : (
            <div className="bg-white p-3 rounded border">
              <SMSCustomerContentWithLinks 
                content={post.content} 
                className="text-gray-900 leading-relaxed"
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Check if this is a personalized SMS
  const isPersonalized = post.marketing_sms_personalized !== false && 
                        (post.content && (post.content.includes('[CUSTOMER_NAME]') || 
                                         post.content.includes('[PET_NAME]') || 
                                         post.content.includes('[PET_NAMES]')));

  return (
    <div className="space-y-4">
      {/* SMS Preview with Real Customer Data */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="text-sm text-orange-600 mb-2 font-medium">
          📱 {isPersonalized ? 'SMS Preview (First Customer)' : 'SMS Preview Content'}
        </div>
        <div className="bg-white p-3 rounded border">
          <SMSCustomerContentWithLinks 
            content={getPreviewContent()} 
            className="text-gray-900 leading-relaxed"
          />
        </div>
        {isPersonalized && (
          <div className="mt-2 text-xs text-orange-600">
            Preview for: {customerPreview.customer_name || 
                        customerPreview.name || 
                        customerPreview.owner_first_name || 
                        customerPreview.first_name || 
                        'Customer'} 
            {customerPreview.phone_number || 
             customerPreview.phone || 
             customerPreview.owner_phone ? 
              ` (${customerPreview.phone_number || customerPreview.phone || customerPreview.owner_phone})` : 
              ' (No phone number)'
            }
          </div>
        )}
      </div>
    </div>
  );
};

const AIInReview = () => {
  const { user, token, isAdmin, loading: authLoading } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [pageReady, setPageReady] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [pageLoading, setPageLoading] = useState(true);
  const [processingPost, setProcessingPost] = useState(null);
  const [filterAgentType, setFilterAgentType] = useState('all');
  const [agentTypes, setAgentTypes] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editForm, setEditForm] = useState({
    content: '',
    image_text: '',
    topic: '',
    email_subject: ''
  });
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);  
  const [deletingPost, setDeletingPost] = useState(null);
  const [postToDelete, setPostToDelete] = useState(null);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [previewModal, setPreviewModal] = useState({ isOpen: false, post: null, platform: null });
  const [smsPreviewModal, setSmsPreviewModal] = useState({ isOpen: false, post: null });
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total_pages: 0,
    total_count: 0,
    has_next: false,
    has_prev: false
  });

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  // Auth check
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = '/login';
    } else if (!authLoading && user) {
      setPageLoading(false);
      // Run all loading operations in parallel
      Promise.all([
        fetchPosts(1, true), // isInitialLoad = true
        fetchAgentTypes()
      ]).then(() => {
        // Mark data as loaded
        setDataLoaded(true);
      }).catch((error) => {
        console.error('Error loading page:', error);
        // Even on error, mark as loaded to prevent infinite loading
        setDataLoaded(true);
      });
    }
  }, [user, authLoading]);

  // Track when posts are actually loaded and available
  useEffect(() => {
    if (dataLoaded && posts !== undefined) {
      // Data is loaded, posts state is ready (even if empty array)
      setImagesLoaded(true); // Will be overridden by image loading logic if there are images
    }
  }, [dataLoaded, posts]);

  // Only set page ready when both data and any images are loaded
  useEffect(() => {
    if (dataLoaded && imagesLoaded && !pageLoading) {
      const timer = setTimeout(() => {
        setPageReady(true);
      }, 100); // Very small delay just to ensure smooth rendering
      
      return () => clearTimeout(timer);
    }
  }, [dataLoaded, imagesLoaded, pageLoading]);

  // Refetch posts when filter changes (but only if page is ready)
  useEffect(() => {
    if (!pageLoading && pageReady) {
      // Reset loading states when filter changes
      setDataLoaded(false);
      setImagesLoaded(false);
      setPageReady(false);
      
      setCurrentPage(1);
      fetchPosts(1).then(() => {
        setDataLoaded(true);
      });
    }
  }, [filterAgentType]);

  // Track image loading
  useEffect(() => {
    if (dataLoaded && posts !== undefined) {
      // Only start image loading detection after data is loaded
      const images = document.querySelectorAll('img[src*="api/files"], img[src*="data:image"]');
      if (images.length === 0) {
        // No images to load, we're ready
        setImagesLoaded(true);
        return;
      }

      let loadedCount = 0;
      const totalImages = images.length;

      const handleImageLoad = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          setImagesLoaded(true);
        }
      };

      const handleImageError = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          setImagesLoaded(true);
        }
      };

      images.forEach(img => {
        if (img.complete) {
          handleImageLoad();
        } else {
          img.addEventListener('load', handleImageLoad);
          img.addEventListener('error', handleImageError);
        }
      });

      // Cleanup
      return () => {
        images.forEach(img => {
          img.removeEventListener('load', handleImageLoad);
          img.removeEventListener('error', handleImageError);
        });
      };
    }
  }, [dataLoaded, posts]);

  const fetchAgentTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai-agent-types`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAgentTypes(data);
        return Promise.resolve();
      } else {
        return Promise.reject('Failed to fetch agent types');
      }
    } catch (error) {
      console.error('Error fetching agent types:', error);
      return Promise.reject(error);
    }
  };

  const handlePageChange = (page) => {
    // Show loading while changing pages
    setDataLoaded(false);
    setImagesLoaded(false);
    setPageReady(false);
    
    setCurrentPage(page);
    fetchPosts(page).then(() => {
      setDataLoaded(true);
    });
  };

  const handlePrevPage = () => {
    if (pagination.has_prev) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination.has_next) {
      handlePageChange(currentPage + 1);
    }
  };

  const downloadImage = async (imageUrl, postId) => {
    try {
      // Fetch the image with proper CORS handling
      const response = await fetch(imageUrl, {
        method: 'GET',
        mode: 'cors',
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch image');
      }
      
      // Convert to blob
      const blob = await response.blob();
      
      // Create object URL
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `post-${postId}-image.jpg`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (err) {
      console.error('Failed to download image:', err);
      // Fallback: open in new tab if download fails
      window.open(imageUrl, '_blank');
    }
  };

  const copyPostContent = async (post) => {
    try {
      // Show copying modal
      setShowCopyModal(true);
      
      // Get the content, preserving emojis and formatting
      let content = post.content || '';
      
      // Add hashtags if present
      if (post.hashtags && post.hashtags.length > 0) {
        const hashtagString = '\n\n' + post.hashtags.map(tag => `#${tag}`).join(' ');
        content += hashtagString;
      }
      
      // Copy to clipboard
      await navigator.clipboard.writeText(content);
      
      // Close modal after a brief moment
      setTimeout(() => {
        setShowCopyModal(false);
      }, 800);
    } catch (err) {
      console.error('Failed to copy:', err);
      setShowCopyModal(false);
      setMessage({ type: 'error', text: 'Failed to copy content' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const openEditModal = (post) => {
    setEditingPost(post);
    setEditForm({
      content: post.content || '',
      image_text: post.image_text || '',
      topic: post.topic || '',
      email_subject: post.email_subject || ''
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingPost(null);
    setEditForm({ content: '', image_text: '', topic: '', email_subject: '' });
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const saveEditedPost = async () => {
    if (!editingPost) return;

    // Save scroll position if modal is open
    const currentScrollPosition = window.scrollY;

    setSaveLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai-posts/${editingPost.id}/edit`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Post updated successfully!' });
        closeEditModal();
        await fetchPosts();
        
        // Restore scroll position after modal closes and data refreshes
        setTimeout(() => {
          window.scrollTo({ top: currentScrollPosition, behavior: 'smooth' });
        }, 300);
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to update post' });
      }
    } catch (error) {
      console.error('Error updating post:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setSaveLoading(false);
    }
  };

  const regenerateContent = async (postId) => {
    // Save current scroll position
    setSavedScrollPosition(window.scrollY);
    
    setShowLoadingModal(true);
    setLoadingMessage('Regenerating content using AI...');
    try {
      // Find the post to get its current content
      const post = posts.find(p => p.id === postId);
      if (!post) {
        setMessage({ type: 'error', text: 'Post not found' });
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/ai-posts/${postId}/regenerate-content`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_content: post.content,
          topic: post.topic || 'social media post'
        })
      });

      if (response.ok) {
        const result = await response.json();
        // Update the post content in the local state
        setPosts(prevPosts => 
          prevPosts.map(p => 
            p.id === postId 
              ? { ...p, content: result.new_content, updated_at: new Date().toISOString() }
              : p
          )
        );
        setMessage({ type: 'success', text: 'Content regenerated successfully! You can now edit further or complete review.' });
        
        // Wait a bit for the UI to update, then restore scroll position
        setTimeout(() => {
          window.scrollTo({ top: savedScrollPosition, behavior: 'smooth' });
          setShowLoadingModal(false);
          setLoadingMessage('');
        }, 500);
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to regenerate content' });
        setShowLoadingModal(false);
        setLoadingMessage('');
      }
    } catch (error) {
      console.error('Error regenerating content:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
      setShowLoadingModal(false);
      setLoadingMessage('');
    }
  };

  const regenerateContentInModal = async (postId) => {
    setShowLoadingModal(true);
    setLoadingMessage('Regenerating content using AI...');
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai-posts/${postId}/regenerate-content`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_content: editForm.content,
          topic: editForm.topic || 'social media post'
        })
      });

      if (response.ok) {
        const result = await response.json();
        // Update the edit form with the new content
        handleEditFormChange('content', result.new_content);
        setMessage({ type: 'success', text: 'Content regenerated successfully!' });
        
        // Wait a bit for the UI to update
        setTimeout(() => {
          setShowLoadingModal(false);
          setLoadingMessage('');
        }, 500);
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to regenerate content' });
        setShowLoadingModal(false);
        setLoadingMessage('');
      }
    } catch (error) {
      console.error('Error regenerating content:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
      setShowLoadingModal(false);
      setLoadingMessage('');
    }
  };

  const regenerateImage = async (postId) => {
    // Save current scroll position
    setSavedScrollPosition(window.scrollY);
    
    setShowLoadingModal(true);
    setLoadingMessage('Regenerating image using AI...');
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai-posts/${postId}/regenerate-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: editForm.content,
          image_text: editForm.image_text
        })
      });

      if (response.ok) {
        const result = await response.json();
        setMessage({ type: 'success', text: 'Image regenerated successfully!' });
        
        // Refresh posts to get the new image
        await fetchPosts();
        
        // Wait for images to load, then restore scroll position
        setTimeout(() => {
          // Check if images are loaded
          const checkImagesLoaded = () => {
            const images = document.querySelectorAll('img[src*="api/files"], img[src*="data:image"]');
            let allLoaded = true;
            
            images.forEach(img => {
              if (!img.complete || img.naturalHeight === 0) {
                allLoaded = false;
              }
            });
            
            if (allLoaded) {
              window.scrollTo({ top: savedScrollPosition, behavior: 'smooth' });
              setShowLoadingModal(false);
              setLoadingMessage('');
            } else {
              // Check again in 200ms
              setTimeout(checkImagesLoaded, 200);
            }
          };
          
          checkImagesLoaded();
        }, 300);
        
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to regenerate image' });
        setShowLoadingModal(false);
        setLoadingMessage('');
      }
    } catch (error) {
      console.error('Error regenerating image:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
      setShowLoadingModal(false);
      setLoadingMessage('');
    }
  };

  const regenerateImageForPost = async (postId) => {
    // Save current scroll position
    setSavedScrollPosition(window.scrollY);
    
    setShowLoadingModal(true);
    setLoadingMessage('Regenerating Image...');
    try {
      // Find the post to get its current content and image_text
      const post = posts.find(p => p.id === postId);
      if (!post) {
        setMessage({ type: 'error', text: 'Post not found' });
        setShowLoadingModal(false);
        setLoadingMessage('');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/ai-posts/${postId}/regenerate-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: post.content,
          image_text: post.image_text || ''
        })
      });

      if (response.ok) {
        const result = await response.json();
        setMessage({ type: 'success', text: 'Image regenerated successfully!' });
        
        // Refresh posts to get the new image
        await fetchPosts();
        
        // Wait for images to load, then restore scroll position
        setTimeout(() => {
          // Check if images are loaded
          const checkImagesLoaded = () => {
            const images = document.querySelectorAll('img[src*="api/files"], img[src*="data:image"]');
            let allLoaded = true;
            
            images.forEach(img => {
              if (!img.complete || img.naturalHeight === 0) {
                allLoaded = false;
              }
            });
            
            if (allLoaded) {
              window.scrollTo({ top: savedScrollPosition, behavior: 'smooth' });
              setShowLoadingModal(false);
              setLoadingMessage('');
            } else {
              // Check again in 200ms
              setTimeout(checkImagesLoaded, 200);
            }
          };
          
          checkImagesLoaded();
        }, 300);
        
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to regenerate image' });
        setShowLoadingModal(false);
        setLoadingMessage('');
      }
    } catch (error) {
      console.error('Error regenerating image:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
      setShowLoadingModal(false);
      setLoadingMessage('');
    }
  };

  const reviewComplete = async (postId) => {
    setProcessingPost(postId);
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai-posts/${postId}/review-complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Post moved to Ready to Publish successfully!' });
        fetchPosts(); // Refresh the list
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to complete review' });
      }
    } catch (error) {
      console.error('Error completing review:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setProcessingPost(null);
    }
  };

  const fetchPosts = async (page = currentPage, isInitialLoad = false) => {
    if (!isInitialLoad) setRefreshLoading(true);
    
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10'
      });
      
      if (filterAgentType !== 'all') {
        params.append('agent_type', filterAgentType);
      }
      
      const url = `${API_BASE_URL}/api/ai-posts/in-review?${params.toString()}`;
        
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts);
        setPagination(data.pagination);
        setCurrentPage(data.pagination.current_page);
        return Promise.resolve();
      } else {
        setMessage({ type: 'error', text: 'Failed to fetch posts' });
        return Promise.reject('Failed to fetch posts');
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
      return Promise.reject(error);
    } finally {
      if (!isInitialLoad) setRefreshLoading(false);
    }
  };

  const openDeleteModal = (post) => {
    setPostToDelete(post);
    setShowDeleteModal(true);  
  };

  const closeDeleteModal = () => {
    setPostToDelete(null);
    setShowDeleteModal(false);
  };

  const confirmDeletePost = async () => {
    if (!postToDelete) return;
    
    setDeletingPost(postToDelete.id);
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai-posts/${postToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Post deleted successfully!' });
        fetchPosts(); // Refresh the posts list
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to delete post' });
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setDeletingPost(null);
      closeDeleteModal();
    }
  };



  const getPlatformIcon = (platform) => {
    const icons = {
      facebook: '📘',
      instagram: '📷',
      twitter: '❌',
      whatsapp: '💬'
    };
    return icons[platform] || '📱';
  };

  if (authLoading || pageLoading || !dataLoaded || !pageReady) {
    return (
      <>
        {/* Show the page content with disabled interactions */}
        <div className={`min-h-screen bg-gray-50 ${!pageReady ? 'pointer-events-none opacity-60' : ''}`}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              {/* Header */}
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Clock className="h-8 w-8 text-orange-600" />
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        In Review
                      </h2>
                      <p className="text-sm text-gray-600">
                        Review AI-generated posts before moving to Ready to Publish
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="text-white px-4 py-2 pr-8 rounded-lg bg-gray-300">
                      Loading filters...
                    </div>
                    <div className="text-white px-4 py-2 rounded-lg bg-gray-300">
                      <RefreshCw className="h-4 w-4 animate-spin mr-2 inline" />
                      Loading...
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Loading content area */}
              <div className="p-6">
                <div className="text-center py-12">
                  <RefreshCw className="h-12 w-12 mx-auto text-theme-blue animate-spin mb-4" />
                  <p className="text-gray-600 text-lg mb-2">Loading your posts...</p>
                  <p className="text-gray-500 text-sm">Please wait while we fetch your AI-generated content</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Header - Compact Design like AI Agents Dashboard */}
            <div className="bg-white border-b border-gray-200">
              <div className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
                      In Review
                      <span className="text-xs font-normal text-gray-500">
                        Total: {posts.length}
                      </span>
                    </h2>
                  </div>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    {/* Agent Type Filter */}
                    <select
                      value={filterAgentType}
                      onChange={(e) => setFilterAgentType(e.target.value)}
                      disabled={!pageReady}
                      className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 transition-colors appearance-none pr-8 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ 
                        backgroundColor: !pageReady ? '#94a3b8' : '#7c3aed',
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                        backgroundPosition: 'right 6px center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '14px'
                      }}
                      onMouseEnter={(e) => {
                        if (pageReady) e.target.style.backgroundColor = '#6d28d9';
                      }}
                      onMouseLeave={(e) => {
                        if (pageReady) e.target.style.backgroundColor = '#7c3aed';
                      }}
                    >
                      <option value="all" style={{ backgroundColor: 'white', color: 'black' }}>All Agent Types</option>
                      {agentTypes.map((type) => (
                        <option key={type.value} value={type.value} style={{ backgroundColor: 'white', color: 'black' }}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    
                    {/* Refresh Button */}
                    <button
                      onClick={() => {
                        // Reset loading states for refresh
                        setDataLoaded(false);
                        setImagesLoaded(false);
                        setPageReady(false);
                        
                        fetchPosts().then(() => {
                          setDataLoaded(true);
                        });
                      }}
                      disabled={refreshLoading || !pageReady}
                      className="text-white text-sm px-3 py-1.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium"
                      style={{ 
                        backgroundColor: (refreshLoading || !pageReady) ? '#94a3b8' : '#f59e0b'
                      }}
                      onMouseEnter={(e) => {
                        if (!refreshLoading && pageReady) e.target.style.backgroundColor = '#d97706';
                      }}
                      onMouseLeave={(e) => {
                        if (!refreshLoading && pageReady) e.target.style.backgroundColor = '#f59e0b';
                      }}
                    >
                      <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="p-6">
              {/* Message Display */}
              {message.text && (
                <div className={`mb-6 rounded-md p-4 ${
                  message.type === 'error' 
                    ? 'bg-red-50 border border-red-200' 
                    : 'bg-green-50 border border-green-200'
                }`}>
                  <div className="flex">
                    <div className="flex-shrink-0">
                      {message.type === 'error' ? (
                        <AlertCircle className="h-5 w-5 text-red-400" />
                      ) : (
                        <CheckCircle className="h-5 w-5 text-green-400" />
                      )}
                    </div>
                    <div className="ml-3">
                      <p className={`text-sm font-medium ${
                        message.type === 'error' ? 'text-red-800' : 'text-green-800'
                      }`}>
                        {message.text}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Posts List */}
              {refreshLoading ? (
                <div className="text-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">Loading posts...</p>
                </div>
              ) : posts.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500 text-lg">No posts in review</p>
                  <p className="text-gray-400 text-sm mt-2">Posts will appear here when AI agents generate content</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {posts.map((post) => (
                    <div key={post.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="p-4">
                        {/* Post Content */}
                        {post.agent_type === 'time_sheet' ? (
                          // Timesheet Post Rendering
                          <div>
                            {/* Timesheet Header */}
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <span className="inline-flex items-center px-3 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    {post.agent_name || 'Timesheet Agent'}
                                  </span>
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                                    <Clock className="h-3 w-3 mr-1" />
                                    Time Sheet
                                  </span>
                                </div>
                                <div className="flex items-center space-x-4 text-sm text-gray-500">
                                  <div className="flex items-center">
                                    <Calendar className="h-4 w-4 mr-1" />
                                    Created: {formatDate(post.created_at)}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Pay Period Date Range */}
                              <div className="text-right">
                                <div className="flex items-center justify-end space-x-2">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                    Pay Period
                                  </span>
                                  <span className="text-sm text-gray-900">
                                    {(() => {
                                      const topic = post.topic || '';
                                      const dateRangeMatch = topic.match(/(\d{4}-\d{2}-\d{2})\s+to\s+(\d{4}-\d{2}-\d{2})/);
                                      if (dateRangeMatch) {
                                        const [, startDate, endDate] = dateRangeMatch;
                                        
                                        // Format dates directly from string without creating Date objects
                                        const formatDateFromString = (dateStr) => {
                                          const [year, month, day] = dateStr.split('-');
                                          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                                                             'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
                                          const monthName = monthNames[parseInt(month) - 1];
                                          const dayNum = parseInt(day);
                                          
                                          const getDaySuffix = (day) => {
                                            if (day >= 11 && day <= 13) return 'th';
                                            switch (day % 10) {
                                              case 1: return 'st';
                                              case 2: return 'nd';
                                              case 3: return 'rd';
                                              default: return 'th';
                                            }
                                          };
                                          
                                          return `${monthName} ${dayNum}${getDaySuffix(dayNum)}`;
                                        };
                                        
                                        const startFormatted = formatDateFromString(startDate);
                                        const endFormatted = formatDateFromString(endDate);
                                        const year = startDate.split('-')[0];
                                        
                                        return `${startFormatted} - ${endFormatted}, ${year}`;
                                      }
                                      return topic.replace('Timesheet Report: ', '');
                                    })()}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Pay Period Summary */}
                            <div className="bg-gray-50 rounded-lg p-4 mb-4">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                                <div>
                                  <div className="text-2xl font-bold text-theme-blue">{post.timesheet_data?.total_employees || 0}</div>
                                  <div className="text-sm text-gray-600">Employees</div>
                                </div>
                                <div>
                                  <div className="text-2xl font-bold text-green-600">{post.timesheet_data?.total_hours?.toFixed(2) || '0.00'}</div>
                                  <div className="text-sm text-gray-600">Total Hours</div>
                                </div>
                                <div>
                                  <div className="text-2xl font-bold text-purple-600">{post.timesheet_data?.regular_hours?.toFixed(2) || '0.00'}</div>
                                  <div className="text-sm text-gray-600">Regular Hours</div>
                                </div>
                                <div>
                                  <div className="text-2xl font-bold text-orange-600">${post.timesheet_data?.total_cost?.toFixed(2) || '0.00'}</div>
                                  <div className="text-sm text-gray-600">Total Pay</div>
                                </div>
                              </div>
                            </div>

                            {/* Employee Dropdown and Actions */}
                            <div className="flex items-center justify-between mb-4">
                              <div>
                                <select
                                  value={selectedEmployee}
                                  onChange={(e) => setSelectedEmployee(e.target.value)}
                                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                                >
                                  <option value="all">All Employees</option>
                                  {post.timesheet_data?.employee_data?.map((employee, index) => (
                                    <option key={index} value={employee.user_name}>
                                      {employee.user_name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            {/* Employee Tables */}
                            <div className="space-y-6">
                              {selectedEmployee === 'all' ? (
                                /* All Employees Summary View */
                                <div className="border border-gray-200 rounded-lg overflow-hidden">
                                  {/* Summary Header */}
                                  <div className="bg-green-50 px-4 py-3 border-b border-gray-200">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <h4 className="text-lg font-semibold text-gray-900">All Employees Summary</h4>
                                        <p className="text-sm text-gray-600">Daily hours breakdown for all employees</p>
                                      </div>
                                      <div className="text-right">
                                        <div className="text-lg font-bold text-green-600">
                                          {post.timesheet_data?.total_hours?.toFixed(2) || '0.00'} hrs
                                        </div>
                                        <div className="text-sm text-gray-600">
                                          ${post.timesheet_data?.total_cost?.toFixed(2) || '0.00'}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Summary Daily Table */}
                                  <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                      <thead className="bg-gray-50">
                                        <tr>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Employees</th>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Hours</th>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Regular Hours</th>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">After Hours</th>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Daily Cost</th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white divide-y divide-gray-200">
                                        {(() => {
                                          // Create summary by date
                                          const dailySummary = {};
                                          
                                          post.timesheet_data?.employee_data?.forEach(employee => {
                                            employee.daily_entries?.forEach(entry => {
                                              const date = entry.date;
                                              if (!dailySummary[date]) {
                                                dailySummary[date] = {
                                                  date: date,
                                                  employeeCount: 0,
                                                  totalHours: 0,
                                                  regularHours: 0,
                                                  afterHours: 0,
                                                  dailyCost: 0
                                                };
                                              }
                                              
                                              dailySummary[date].employeeCount += 1;
                                              dailySummary[date].totalHours += entry.total_hours || 0;
                                              dailySummary[date].regularHours += entry.regular_hours || 0;  
                                              dailySummary[date].afterHours += entry.after_hours || 0;
                                              dailySummary[date].dailyCost += ((entry.regular_hours || 0) * employee.hourly_rate) + ((entry.after_hours || 0) * employee.after_hours_rate);
                                            });
                                          });
                                          
                                          return Object.values(dailySummary).map((daySummary, index) => (
                                            <tr key={index} className="hover:bg-gray-50">
                                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {new Date(daySummary.date).toLocaleDateString()}
                                              </td>
                                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {daySummary.employeeCount}
                                              </td>
                                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {daySummary.totalHours.toFixed(2)}h
                                              </td>
                                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {daySummary.regularHours.toFixed(2)}h
                                              </td>
                                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {daySummary.afterHours.toFixed(2)}h
                                              </td>
                                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                ${daySummary.dailyCost.toFixed(2)}
                                              </td>
                                            </tr>
                                          ));
                                        })()}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ) : (
                                /* Individual Employee View */
                                post.timesheet_data?.employee_data?.filter(emp => emp.user_name === selectedEmployee).map((employee, empIndex) => (
                                  <div key={empIndex} className="border border-gray-200 rounded-lg overflow-hidden">
                                    {/* Employee Header */}
                                    <div className="bg-blue-50 px-4 py-3 border-b border-gray-200">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <h4 className="text-lg font-semibold text-gray-900">{employee.user_name}</h4>
                                          <p className="text-sm text-gray-600">{employee.user_role ? employee.user_role.charAt(0).toUpperCase() + employee.user_role.slice(1).toLowerCase() : 'Employee'}</p>
                                        </div>
                                        <div className="text-right">
                                          <div className="text-lg font-bold text-theme-blue">{employee.total_hours?.toFixed(2) || '0.00'} hrs</div>
                                          <div className="text-sm text-gray-600">${employee.total_pay?.toFixed(2) || '0.00'}</div>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Daily Hours Table */}
                                    <div className="overflow-x-auto">
                                      <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                          <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Regular Hours</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">After Hours</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Hours</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Regular Rate</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">After Hours Rate</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Daily Pay</th>
                                          </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                          {employee.daily_entries?.map((entry, dayIndex) => (
                                            <tr key={dayIndex} className="hover:bg-gray-50">
                                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {new Date(entry.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                              </td>
                                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.regular_hours?.toFixed(2) || '0.00'}</td>
                                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.after_hours?.toFixed(2) || '0.00'}</td>
                                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{entry.total_hours?.toFixed(2) || '0.00'}</td>
                                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${employee.hourly_rate?.toFixed(2) || '0.00'}</td>
                                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${employee.after_hours_rate?.toFixed(2) || '0.00'}</td>
                                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                ${((entry.regular_hours * employee.hourly_rate) + (entry.after_hours * employee.after_hours_rate)).toFixed(2)}
                                              </td>
                                            </tr>
                                          ))}
                                          {/* Employee Total Row */}
                                          <tr className="bg-blue-50 border-t-2 border-blue-200">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-900">Total:</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-900">{employee.regular_hours?.toFixed(2) || '0.00'}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-900">{employee.after_hours_hours?.toFixed(2) || '0.00'}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-900">{employee.total_hours?.toFixed(2) || '0.00'}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">-</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">-</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-900">${employee.total_pay?.toFixed(2) || '0.00'}</td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )))}
                            </div>
                          </div>
                        ) : (
                          // Social Media Post Rendering
                          <div>
                            {/* Social Media Post Header */}
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <span className="inline-flex items-center px-3 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    {post.agent_name || 'AI Agent'}
                                  </span>
                                  {/* Show specific channel for marketing agents or default agent type */}
                                  {post.agent_type === 'marketing_agent' && post.marketing_channel ? (
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${
                                      post.marketing_channel === 'social_media' ? 'bg-teal-100 text-teal-700' :
                                      post.marketing_channel === 'email' ? 'bg-purple-100 text-purple-700' :
                                      post.marketing_channel === 'sms' ? 'bg-orange-100 text-orange-700' :
                                      'bg-pink-100 text-pink-700'
                                    }`}>
                                      {post.marketing_channel === 'social_media' ? (post.platform ? post.platform.charAt(0).toUpperCase() + post.platform.slice(1) : 'Social Media') :
                                       post.marketing_channel === 'email' ? 'Email' :
                                       post.marketing_channel === 'sms' ? 'SMS' :
                                       post.marketing_channel}
                                    </span>
                                  ) : post.agent_type && (
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${
                                      post.agent_type === 'social_media' ? 'bg-teal-100 text-teal-700' :
                                      post.agent_type === 'email' ? 'bg-purple-100 text-purple-700' :
                                      post.agent_type === 'email_agent' ? 'bg-purple-100 text-purple-700' :
                                      post.agent_type === 'sms_agent' ? 'bg-orange-100 text-orange-700' :
                                      post.agent_type === 'paystub_agent' ? 'bg-blue-100 text-blue-700' :
                                      'bg-gray-100 text-gray-700'
                                      }`}>
                                      {post.agent_type === 'social_media' ? 'Social Media' :
                                       post.agent_type === 'email' ? 'Email' :
                                       post.agent_type === 'email_agent' ? 'Email' :
                                       post.agent_type === 'sms_agent' ? 'SMS' :
                                       post.agent_type === 'marketing_agent' ? 'Marketing' :
                                       post.agent_type === 'paystub_agent' ? 'Paystub' :
                                       post.agent_type}
                                    </span>
                                  )}
                                  {/* Only show topic for non-paystub posts */}
                                  {post.agent_type !== 'paystub_agent' && (
                                    <>
                                      <span className="text-gray-500 text-sm">•</span>
                                      <span className="text-gray-500 text-sm">{post.topic}</span>
                                    </>
                                  )}
                                </div>
                                <div className="flex items-center space-x-4 text-sm text-gray-500">
                                  <div className="flex items-center">
                                    <Calendar className="h-4 w-4 mr-1" />
                                    {post.updated_at && post.updated_at !== post.created_at ? 
                                      `Updated: ${formatDate(post.updated_at)}` : 
                                      `Created: ${formatDate(post.created_at)}`
                                    }
                                  </div>
                                  {post.scheduled_for && (
                                    <div className="flex items-center">
                                      <Clock className="h-4 w-4 mr-1" />
                                      Scheduled: {formatScheduledDate(post.scheduled_for)}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                {/* Marketing Agent Platform Display */}
                                {post.agent_type === 'marketing_agent' && post.marketing_channel === 'social_media' && post.platform ? (
                                  <div className="flex items-center space-x-1">
                                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                                      {getPlatformIcon(post.platform)} {post.platform === 'twitter' ? 'X' : post.platform.charAt(0).toUpperCase() + post.platform.slice(1)}
                                    </span>
                                    <button
                                      onClick={() => setPreviewModal({ isOpen: true, post, platform: post.platform })}
                                      className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                                      title={`Preview on ${post.platform}`}
                                    >
                                      <Eye className="h-3 w-3 mr-1" />
                                      Preview
                                    </button>
                                  </div>
                                ) : 
                                /* Marketing Agent Email Display - Badge only, NO preview button */
                                post.agent_type === 'marketing_agent' && post.marketing_channel === 'email' ? (
                                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                                    📧 Email
                                  </span>
                                ) :
                                /* Regular Email Agents Display - Badge only, NO preview button */
                                (post.agent_type === 'email' || post.agent_type === 'email_agent') ? (
                                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                                    📧 Email
                                  </span>
                                ) :
                                /* Paystub Agent Display - Pay Period instead of badge */
                                post.agent_type === 'paystub_agent' ? (
                                  (() => {
                                    try {
                                      const paystubData = post.paystub_data || {};
                                      const startDate = paystubData.start_date;
                                      const endDate = paystubData.end_date;
                                      
                                      return startDate && endDate ? (
                                        <div className="flex items-center space-x-2">
                                          <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                            Pay Period
                                          </span>
                                          <span className="text-sm text-gray-900">
                                            {formatFriendlyDateRange(startDate, endDate)}
                                          </span>
                                        </div>
                                      ) : (
                                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-700">
                                          Paystub
                                        </span>
                                      );
                                    } catch (error) {
                                      return (
                                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-700">
                                          Paystub
                                        </span>
                                      );
                                    }
                                  })()
                                ) :
                                /* Regular platforms display */
                                post.platforms && post.platforms.map((platform) => (
                                  <div key={platform} className="flex items-center space-x-1">
                                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                                      {getPlatformIcon(platform)} {platform === 'twitter' ? 'X' : platform.charAt(0).toUpperCase() + platform.slice(1)}
                                    </span>
                                    <button
                                      onClick={() => setPreviewModal({ isOpen: true, post, platform })}
                                      className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                                      title={`Preview on ${platform}`}
                                    >
                                      <Eye className="h-3 w-3 mr-1" />
                                      Preview
                                    </button>
                                  </div>
                                ))}
                                
                                {/* SMS Preview Button - Same pattern as social media */}
                                {(post.agent_type === 'sms_agent' || (post.agent_type === 'marketing_agent' && post.marketing_channel === 'sms')) && (
                                  <div className="flex items-center space-x-1">
                                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                                      📱 SMS
                                    </span>
                                    <button
                                      onClick={() => setSmsPreviewModal({ isOpen: true, post })}
                                      className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                                      title="Preview SMS"
                                    >
                                      <Eye className="h-3 w-3 mr-1" />
                                      Preview
                                    </button>
                                  </div>
                                )}
                                
                                {/* Marketing Agent Channel-specific info */}
                                {post.agent_type === 'marketing_agent' && (
                                  <>
                                    {post.marketing_channel === 'email' && post.email_personalized && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-700">
                                        📧 Personalized
                                      </span>
                                    )}
                                    {post.marketing_channel === 'sms' && post.sms_personalized && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-700">
                                        📱 Personalized
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Post Content - with channel-specific formatting */}
                            <div className="prose max-w-none">
                              {/* Marketing Agent Email Post */}
                              {post.agent_type === 'marketing_agent' && post.marketing_channel === 'email' ? (
                                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                                  <div className="flex items-center space-x-2 mb-3">
                                    <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                                    <span className="text-sm font-medium text-gray-700">Email Preview</span>
                                  </div>
                                  
                                  {/* Email Header (like real email clients) */}
                                  <div className="bg-gray-50 rounded-t-lg border border-gray-200 px-4 py-3">
                                    <div className="space-y-2 text-sm">
                                      <div className="flex">
                                        <span className="text-gray-500 font-medium w-16">From:</span>
                                        <span className="text-gray-900">Pets and Vets Animal Hospital &lt;vet@petsandvetsanimalhospital.com&gt;</span>
                                      </div>
                                      <div className="flex">
                                        <span className="text-gray-500 font-medium w-16">To:</span>
                                        <span className="text-gray-900">
                                          {post.marketing_email_personalized === false ? 
                                            'All Customers' : 
                                            (post.sample_customer_name && post.sample_customer_email ? 
                                              `${post.sample_customer_name} <${post.sample_customer_email}>` : 
                                              'All Customers'
                                            )
                                          }
                                        </span>
                                      </div>
                                      <div className="flex items-start">
                                        <span className="text-gray-500 font-medium w-16">Subject:</span>
                                        <div className="flex-1">
                                          <span className="text-gray-900 font-medium">
                                            {post.email_subject || 'No Subject'}
                                          </span>
                                          {post.email_subject && post.email_subject.length > 100 && (
                                            <span className="text-orange-500 text-xs ml-2">
                                              ({post.email_subject.length} chars - may be truncated)
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Email Body */}
                                  <div className="bg-white rounded-b-lg border-x border-b border-gray-200 p-4">
                                    <ContentWithLinks 
                                      content={post.content} 
                                      className="text-sm text-gray-900 leading-relaxed"
                                    />
                                  </div>
                                </div>
                              ) : 
                              /* Marketing Agent SMS Post */
                              post.agent_type === 'marketing_agent' && post.marketing_channel === 'sms' ? (
                                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                                  <div className="flex items-center space-x-2 mb-3">
                                    <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                                    <span className="text-sm font-medium text-gray-700">SMS Content</span>
                                  </div>
                                  
                                  {/* Content Summary */}
                                  <div className="bg-gray-50 rounded-lg p-3">
                                    <SMSContentWithShortLinks 
                                      content={post.content} 
                                      className="text-sm text-gray-700 line-clamp-2"
                                    />
                                    <div className="text-xs text-gray-500 mt-1">
                                      {post.content ? `${post.content.length}/160 characters` : '0/160 characters'}
                                    </div>
                                  </div>
                                </div>
                              ) : 
                              /* Regular Email Agent Posts */
                              (post.agent_type === 'email' || post.agent_type === 'email_agent') ? (
                                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                                  <div className="flex items-center space-x-2 mb-3">
                                    <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                                    <span className="text-sm font-medium text-gray-700">Email Preview</span>
                                  </div>
                                  
                                  {/* Email Header (like real email clients) */}
                                  <div className="bg-gray-50 rounded-t-lg border border-gray-200 px-4 py-3">
                                    <div className="space-y-2 text-sm">
                                      <div className="flex">
                                        <span className="text-gray-500 font-medium w-16">From:</span>
                                        <span className="text-gray-900">Pets and Vets Animal Hospital &lt;vet@petsandvetsanimalhospital.com&gt;</span>
                                      </div>
                                      <div className="flex">
                                        <span className="text-gray-500 font-medium w-16">To:</span>
                                        <span className="text-gray-900">
                                          {post.sms_personalized === false || post.email_personalized === false ? 
                                            'All Customers' : 
                                            (post.sample_customer_name && post.sample_customer_email ? 
                                              `${post.sample_customer_name} <${post.sample_customer_email}>` : 
                                              'All Customers'
                                            )
                                          }
                                        </span>
                                      </div>
                                      <div className="flex items-start">
                                        <span className="text-gray-500 font-medium w-16">Subject:</span>
                                        <div className="flex-1">
                                          <span className="text-gray-900 font-medium">
                                            {post.email_subject || 'No Subject'}
                                          </span>
                                          {post.email_subject && post.email_subject.length > 100 && (
                                            <span className="text-orange-500 text-xs ml-2">
                                              ({post.email_subject.length} chars - may be truncated)
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Email Body - Use HTML if available, otherwise plain text */}
                                  <div className="bg-white rounded-b-lg border-x border-b border-gray-200 p-4">
                                    {post.email_html ? (
                                      <div 
                                        dangerouslySetInnerHTML={{ __html: post.email_html }}
                                        className="email-content"
                                      />
                                    ) : (
                                      <ContentWithLinks 
                                        content={post.content} 
                                        className="text-sm text-gray-900 leading-relaxed"
                                      />
                                    )}
                                  </div>
                                </div>
                              ) : 
                                            post.agent_type === 'paystub_agent' ? (
                      (() => {
                        try {
                          const paystubData = post.paystub_data || {};
                          const startDate = paystubData.start_date;
                          const endDate = paystubData.end_date;
                          const employeePaystubs = paystubData.employee_paystubs || [];
                          
                          // Calculate totals
                          const totalEmployees = employeePaystubs.length;
                          const totalGrossPay = employeePaystubs.reduce((sum, emp) => sum + (emp.gross_pay || 0), 0);
                          const totalDeductions = employeePaystubs.reduce((sum, emp) => sum + (emp.total_deductions || 0), 0);
                          const totalNetPay = employeePaystubs.reduce((sum, emp) => sum + (emp.net_pay || 0), 0);
                          
                          return (
                            <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center space-x-2">
                                  <div className="w-3 h-3 rounded-full bg-theme-blue"></div>
                                  <span className="text-sm font-medium text-gray-700">Paystub Summary</span>
                                </div>
                              </div>
                              
                              <div className="space-y-4">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                                    <div className="text-lg font-bold text-theme-blue">{totalEmployees}</div>
                                    <div className="text-xs text-blue-800">Employees</div>
                                  </div>
                                  
                                  <div className="bg-green-50 p-3 rounded-lg text-center">
                                    <div className="text-lg font-bold text-green-600">${totalGrossPay.toFixed(2)}</div>
                                    <div className="text-xs text-green-800">Total Gross</div>
                                  </div>
                                  
                                  <div className="bg-red-50 p-3 rounded-lg text-center">
                                    <div className="text-lg font-bold text-red-600">${totalDeductions.toFixed(2)}</div>
                                    <div className="text-xs text-red-800">Total Deductions</div>
                                  </div>
                                  
                                  <div className="bg-purple-50 p-3 rounded-lg text-center">
                                    <div className="text-lg font-bold text-purple-600">${totalNetPay.toFixed(2)}</div>
                                    <div className="text-xs text-purple-800">Total Net Pay</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        } catch (error) {
                          console.error('Error parsing paystub data:', error);
                          return (
                            <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                              <div className="text-center py-4">
                                <div className="text-sm text-gray-500">Paystub data not available</div>
                              </div>
                            </div>
                          );
                        }
                      })()
                    ) :
                              /* Regular SMS Agent Posts */
                              post.agent_type === 'sms_agent' ? (
                                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                                  <div className="flex items-center space-x-2 mb-3">
                                    <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                                    <span className="text-sm font-medium text-gray-700">SMS Content</span>
                                  </div>
                                  
                                  {/* SMS Content and Image Layout */}
                                  {post.image_url ? (
                                    /* 2-column layout: Content left, Image right */
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                      {/* Content Column (left) - takes 2/3 of width */}
                                      <div className="lg:col-span-2">
                                        <div className="bg-gray-50 rounded-lg p-3">
                                          <SMSContentWithShortLinks 
                                            content={post.content} 
                                            className="text-sm text-gray-700 line-clamp-2"
                                          />
                                          <div className="text-xs text-gray-500 mt-1">
                                            {post.content ? `${post.content.length}/160 characters` : '0/160 characters'}
                                          </div>
                                        </div>
                                      </div>
                                      
                                      {/* Image Column (right) - takes 1/3 of width */}
                                      <div className="lg:col-span-1">
                                        <h4 className="text-sm font-medium text-gray-900 mb-2">AI Generated Image:</h4>
                                        <div className="relative group">
                                          <img 
                                            src={post.image_url} 
                                            alt="SMS content" 
                                            className="w-full h-auto rounded-lg border border-gray-200"
                                            style={{ maxHeight: '200px', objectFit: 'cover' }}
                                            onLoad={() => {
                                              const allImages = document.querySelectorAll('img');
                                              const loadedImages = Array.from(allImages).filter(img => img.complete);
                                              if (loadedImages.length === allImages.length) {
                                                setImagesLoaded(true);
                                              }
                                            }}
                                          />
                                          <button
                                            onClick={() => downloadImage(post.image_url, post.id)}
                                            className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
                                            title="Download Image"
                                          >
                                            <Download className="h-4 w-4 text-gray-700" />
                                          </button>
                                        </div>
                                        
                                        {post.image_text && (
                                          <p className="text-xs text-gray-600 mt-2 italic">
                                            Requested: {post.image_text}
                                          </p>
                                        )}
                                        
                                        {/* Regenerate Image Button */}
                                        <button
                                          onClick={() => regenerateImageForPost(post.id)}
                                          disabled={showLoadingModal || !pageReady}
                                          className="inline-flex items-center px-3 py-1.5 mt-3 w-full border border-purple-300 text-sm leading-4 font-medium rounded-md text-purple-700 bg-purple-50 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 justify-center"
                                        >
                                          {showLoadingModal ? (
                                            <>
                                              <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                                              Regenerating...
                                            </>
                                          ) : (
                                            <>
                                              <ImageIcon className="h-4 w-4 mr-1" />
                                              Regenerate Image
                                            </>
                                          )}
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    /* Content only (no image) */
                                    <div className="bg-gray-50 rounded-lg p-3">
                                      <SMSContentWithShortLinks 
                                        content={post.content} 
                                        className="text-sm text-gray-700 line-clamp-2"
                                      />
                                      <div className="text-xs text-gray-500 mt-1">
                                        {post.content ? `${post.content.length}/160 characters` : '0/160 characters'}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : 
                              /* Default Content Display - 2-column layout with text and image */
                              (
                                post.image_url ? (
                                  /* 2-column layout: Content left, Image right */
                                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    {/* Content Column (left) - takes 2/3 of width */}
                                    <div className="lg:col-span-2 whitespace-pre-wrap text-sm text-gray-900 leading-relaxed">
                                      {post.content}
                                    </div>
                                    
                                    {/* Image Column (right) - takes 1/3 of width */}
                                    <div className="lg:col-span-1">
                                      <h4 className="text-sm font-medium text-gray-900 mb-2">AI Generated Image:</h4>
                                      <div className="relative group">
                                        <img 
                                          src={post.image_url} 
                                          alt="Post content" 
                                          className="w-full h-auto rounded-lg border border-gray-200"
                                          style={{ maxHeight: '200px', objectFit: 'cover' }}
                                          onLoad={() => {
                                            // Check if all images have loaded and set imagesLoaded state
                                            const allImages = document.querySelectorAll('img');
                                            const loadedImages = Array.from(allImages).filter(img => img.complete);
                                            if (loadedImages.length === allImages.length) {
                                              setImagesLoaded(true);
                                            }
                                          }}
                                        />
                                        <button
                                          onClick={() => downloadImage(post.image_url, post.id)}
                                          className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
                                          title="Download Image"
                                        >
                                          <Download className="h-4 w-4 text-gray-700" />
                                        </button>
                                      </div>
                                      
                                      {post.image_text && (
                                        <p className="text-xs text-gray-600 mt-2 italic">
                                          Requested: {post.image_text}
                                        </p>
                                      )}
                                      
                                      {/* Regenerate Image Button */}
                                      <button
                                        onClick={() => regenerateImageForPost(post.id)}
                                        disabled={showLoadingModal || !pageReady}
                                        className="inline-flex items-center px-3 py-1.5 mt-3 w-full border border-purple-300 text-sm leading-4 font-medium rounded-md text-purple-700 bg-purple-50 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 justify-center"
                                      >
                                        {showLoadingModal ? (
                                          <>
                                            <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                                            Regenerating...
                                          </>
                                        ) : (
                                          <>
                                            <ImageIcon className="h-4 w-4 mr-1" />
                                            Regenerate Image
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  /* Single column layout for posts without images */
                                  <div className="whitespace-pre-wrap text-sm text-gray-900 leading-relaxed">
                                    {post.content}
                                  </div>
                                )
                              )}
                              
                              {/* Remove the separate image section since it's now integrated above */}
                              
                              {post.hashtags && post.hashtags.length > 0 && (
                                <div className="mt-4 flex flex-wrap gap-2">
                                  {post.hashtags.map((hashtag, index) => (
                                    <span 
                                      key={index}
                                      className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700"
                                    >
                                      #{hashtag}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                        )}

                        {/* Action Buttons */}
                        <div className="flex items-center justify-end space-x-2 mt-6 pt-4 border-t border-gray-200">
                          {post.agent_type === 'time_sheet' ? (
                            /* Timesheet Actions */
                            <>
                              <button
                                onClick={() => openDeleteModal(post)}
                                disabled={!pageReady}
                                className="flex items-center px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                              >
                                <Trash2 className="h-4 w-4 mr-1.5" />
                                Delete
                              </button>
                              <button
                                onClick={() => copyPostContent(post)}
                                disabled={!pageReady}
                                className="flex items-center px-3 py-1.5 text-sm border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                              >
                                <Copy className="h-4 w-4 mr-1.5" />
                                Copy Content
                              </button>
                              <button
                                onClick={() => window.location.href = `/timesheet-hours-adjustment/${post.id}?from=in-review`}
                                disabled={!pageReady}
                                className="flex items-center px-3 py-1.5 text-sm border border-orange-300 text-orange-700 bg-orange-50 rounded-md hover:bg-orange-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                              >
                                <Edit3 className="h-4 w-4 mr-1.5" />
                                Adjust Hours
                              </button>
                              <button
                                onClick={() => reviewComplete(post.id)}
                                disabled={processingPost === post.id || !pageReady}
                                className="flex items-center px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                              >
                                {processingPost === post.id ? (
                                  <>
                                    <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />
                                    Processing...
                                  </>
                                ) : (
                                  <>
                                    <ThumbsUp className="h-4 w-4 mr-1.5" />
                                    Review Complete
                                  </>
                                )}
                              </button>
                            </>
                          ) : post.agent_type === 'paystub_agent' ? (
                            /* Paystub Actions - View Full Report, Delete, Review Complete */
                            <>
                              {/* View Full Report button */}
                              <button
                                onClick={() => window.location.href = `/paystub-report/${post.id}`}
                                disabled={!pageReady}
                                className="flex items-center px-3 py-1.5 text-sm border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                              >
                                <Eye className="h-4 w-4 mr-1.5" />
                                View Full Report
                              </button>
                              
                              {/* Delete button */}
                              <button
                                onClick={() => openDeleteModal(post)}
                                disabled={!pageReady}
                                className="flex items-center px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                              >
                                <Trash2 className="h-4 w-4 mr-1.5" />
                                Delete
                              </button>
                              
                              {/* Review Complete button */}
                              <button
                                onClick={() => reviewComplete(post.id)}
                                disabled={processingPost === post.id || !pageReady}
                                className="flex items-center px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                              >
                                {processingPost === post.id ? (
                                  <>
                                    <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />
                                    Processing...
                                  </>
                                ) : (
                                  <>
                                    <ThumbsUp className="h-4 w-4 mr-1.5" />
                                    Review Complete
                                  </>
                                )}
                              </button>
                            </>
                          ) :
                          
                          (
                            /* Regular Social Media Actions - Exclude paystub posts from regenerate content */
                            <>
                              {/* Only show Regenerate Content for non-paystub posts */}
                              {post.agent_type !== 'paystub_agent' && (
                                <button
                                  onClick={() => regenerateContent(post.id)}
                                  disabled={showLoadingModal || !pageReady}
                                  className="flex items-center px-3 py-1.5 text-sm border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                                >
                                  <MessageSquare className="h-4 w-4 mr-1.5" />
                                  Regenerate Content
                                </button>
                              )}
                              <button
                                onClick={() => copyPostContent(post)}
                                disabled={!pageReady}
                                className="flex items-center px-3 py-1.5 text-sm border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                              >
                                <Copy className="h-4 w-4 mr-1.5" />
                                Copy Content
                              </button>
                              <button
                                onClick={() => openEditModal(post)}
                                disabled={!pageReady}
                                className="flex items-center px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                              >
                                <Edit3 className="h-4 w-4 mr-1.5" />
                                Edit
                              </button>
                              <button
                                onClick={() => openDeleteModal(post)}
                                disabled={!pageReady}
                                className="flex items-center px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                              >
                                <Trash2 className="h-4 w-4 mr-1.5" />
                                Delete
                              </button>
                              <button
                                onClick={() => reviewComplete(post.id)}
                                disabled={processingPost === post.id || !pageReady}
                                className="flex items-center px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                              >
                                {processingPost === post.id ? (
                                  <>
                                    <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />
                                    Processing...
                                  </>
                                ) : (
                                  <>
                                    <ThumbsUp className="h-4 w-4 mr-1.5" />
                                    Review Complete
                                  </>
                                )}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {posts.length > 0 && pagination.total_pages > 1 && (
                <div className="mt-8 flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, pagination.total_count)} of {pagination.total_count} posts
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handlePrevPage}
                      disabled={!pagination.has_prev}
                      className="flex items-center px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </button>
                    
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                              currentPage === page
                                ? 'bg-theme-blue text-white'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      {pagination.total_pages > 5 && (
                        <>
                          <span className="px-2 text-gray-500">...</span>
                          <button
                            onClick={() => handlePageChange(pagination.total_pages)}
                            className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                              currentPage === pagination.total_pages
                                ? 'bg-theme-blue text-white'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            {pagination.total_pages}
                          </button>
                        </>
                      )}
                    </div>
                    
                    <button
                      onClick={handleNextPage}
                      disabled={!pagination.has_next}
                      className="flex items-center px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Edit Modal with Sticky Header/Footer */}
        {showEditModal && editingPost && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
              {/* Sticky Header */}
              <div className="px-6 py-4 border-b border-gray-200 rounded-t-lg" style={{ backgroundColor: 'var(--theme-primary)' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-white bg-opacity-20">
                      <Edit3 className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white">
                        Edit Post
                      </h3>
                      <p className="text-white text-opacity-80 text-sm mt-1">
                        Modify content and settings
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={closeEditModal}
                    className="text-white hover:text-white hover:text-opacity-80 transition-colors p-1 rounded-full hover:bg-white hover:bg-opacity-20"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto px-6 py-6">
                <div className="space-y-6">
                  {/* Topic */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Topic</label>
                    <input
                      type="text"
                      value={editForm.topic}
                      onChange={(e) => handleEditFormChange('topic', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Post topic"
                    />
                  </div>

                  {/* Email Subject - only show for email posts */}
                  {(editingPost.agent_type === 'email' || editingPost.agent_type === 'email_agent' || 
                    (editingPost.agent_type === 'marketing_agent' && editingPost.marketing_channel === 'email')) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Subject <span className="text-gray-500 text-xs">(max 100 characters)</span>
                      </label>
                      <input
                        type="text"
                        value={editForm.email_subject}
                        onChange={(e) => handleEditFormChange('email_subject', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter email subject line"
                        maxLength={100}
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        {editForm.email_subject.length}/100 characters
                      </div>
                    </div>
                  )}

                  {/* Content */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">Content</label>
                      <button
                        onClick={() => regenerateContentInModal(editingPost.id)}
                        disabled={showLoadingModal}
                        className="flex items-center px-3 py-1.5 bg-theme-blue text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                      >
                        <MessageSquare className="h-3 w-3 mr-1.5" />
                        Regenerate Content
                      </button>
                    </div>
                    <textarea
                      value={editForm.content}
                      onChange={(e) => handleEditFormChange('content', e.target.value)}
                      rows={8}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Post content"
                    />
                  </div>

                  {/* Image Text Overlay - hide for SMS agents */}
                  {editingPost.agent_type !== 'sms_agent' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Text Overlay on Image
                        <span className="text-gray-500 text-xs ml-1">(appears as semi-transparent box)</span>
                      </label>
                      <input
                        type="text"
                        value={editForm.image_text}
                        onChange={(e) => handleEditFormChange('image_text', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Call Now, Book Appointment, etc."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        This text will appear in a semi-transparent dark box over the image (like 'Call Now' button text)
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Sticky Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={closeEditModal}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveEditedPost}
                    disabled={saveLoading}
                    className="px-6 py-2 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    style={{ backgroundColor: saveLoading ? '#94a3b8' : 'var(--theme-primary)' }}
                    onMouseEnter={(e) => {
                      if (!saveLoading) e.target.style.backgroundColor = 'var(--theme-primary-hover)';
                    }}
                    onMouseLeave={(e) => {
                      if (!saveLoading) e.target.style.backgroundColor = 'var(--theme-primary)';
                    }}
                  >
                    {saveLoading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Compact AI Operation Loading Modal */}
        {showLoadingModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg shadow-2xl p-6 max-w-sm w-full mx-4">
              <div className="text-center">
                <div className="mb-3">
                  <RefreshCw className="h-10 w-10 mx-auto text-theme-blue animate-spin" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Processing</h3>
                <p className="text-gray-600 text-sm mb-2">{loadingMessage}</p>
                {loadingMessage.includes('image') && (
                  <p className="text-xs text-gray-500 mb-2">
                    Waiting for new image to load...
                  </p>
                )}
                <div className="text-xs text-gray-500">
                  Please wait - this may take a few moments
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Copy Modal */}
        {showCopyModal && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl px-6 py-4 mx-4 flex items-center space-x-3">
              <RefreshCw className="h-5 w-5 text-theme-blue animate-spin" />
              <span className="text-gray-900 font-medium">Copying...</span>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && postToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Delete Post</h3>
              </div>
              <div className="px-6 py-4">
                <p className="text-gray-600 mb-4">
                  Are you sure you want to delete this post? This action cannot be undone.
                </p>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="font-medium text-gray-900 text-sm">{postToDelete.topic}</p>
                  <p className="text-gray-500 text-sm mt-1 line-clamp-2">{postToDelete.content}</p>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={closeDeleteModal}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeletePost}
                    disabled={deletingPost === postToDelete.id}
                    className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center"
                  >
                    {deletingPost === postToDelete.id ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Post
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Social Media Preview Modal */}
        <SocialMediaPreviewModal
          isOpen={previewModal.isOpen}
          onClose={() => setPreviewModal({ isOpen: false, post: null, platform: null })}
          post={previewModal.post}
          platform={previewModal.platform}
        />

        {/* SMS Preview Modal */}
        <SMSPreviewModal
          isOpen={smsPreviewModal.isOpen}
          onClose={() => setSmsPreviewModal({ isOpen: false, post: null })}
          post={smsPreviewModal.post}
        />
      </div>
    </>
  );
};

export default AIInReview;